import React, { useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, User2, LogOut, Menu as MenuIcon, X, ClipboardList, Shield, LogIn } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

const LOGO = "https://customer-assets.emergentagent.com/job_korean-food-hub-1/artifacts/ghexjw3c_mokjaa.png";

function MobileDrawer({ open, onClose, user, logout }) {
  const navigate = useNavigate();
  if (!open) return null;
  const handleLogout = () => { logout(); onClose(); navigate("/"); };

  return createPortal(
    <div className="md:hidden fixed inset-0 z-[100]" data-testid="mobile-nav-drawer" style={{ width: "100vw", height: "100vh" }}>
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="absolute right-0 top-0 bottom-0 w-[84%] max-w-sm bg-white shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-5 h-16 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-2">
            <img src={LOGO} alt="Mok.Jaa" className="h-8 w-8 rounded-full bg-black" />
            <div className="font-display font-black text-lg">Mok.Jaa</div>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-2 hover:bg-gray-100"
            aria-label="Close menu"
            data-testid="mobile-nav-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {user && (
          <div className="px-5 py-4 border-b border-gray-100 shrink-0">
            <div className="text-[10px] uppercase tracking-widest font-bold text-gray-400">Signed in as</div>
            <div className="text-sm font-bold mt-1 truncate">{user?.name || user?.email}</div>
            <div className="text-xs text-gray-500 truncate">{user?.email}</div>
          </div>
        )}

        <nav className="flex-1 overflow-y-auto py-2">
          <Link
            to="/menu"
            onClick={onClose}
            className="flex items-center gap-3 px-5 py-4 hover:bg-gray-50 border-b border-gray-50"
            data-testid="mobile-nav-menu"
          >
            <MenuIcon className="w-5 h-5" />
            <span className="font-bold">Menu</span>
          </Link>
          <Link
            to="/account"
            onClick={onClose}
            className="flex items-center gap-3 px-5 py-4 hover:bg-gray-50 border-b border-gray-50"
            data-testid="mobile-nav-orders"
          >
            <ClipboardList className="w-5 h-5" />
            <span className="font-bold">My Orders & Tracking</span>
          </Link>
          <a
            href="/admin/login"
            onClick={onClose}
            className="flex items-center gap-3 px-5 py-4 hover:bg-gray-50 border-b border-gray-50 text-gray-600"
            data-testid="mobile-nav-admin"
          >
            <Shield className="w-5 h-5" />
            <span className="font-bold">Admin</span>
          </a>
        </nav>

        <div className="border-t border-gray-100 p-5 shrink-0">
          {user ? (
            <button
              onClick={handleLogout}
              className="w-full rounded-full border-2 border-black px-5 py-3 font-bold text-sm inline-flex items-center justify-center gap-2"
              data-testid="mobile-nav-logout"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          ) : (
            <Link
              to="/auth"
              onClick={onClose}
              className="w-full bg-black text-white rounded-full px-5 py-3 font-bold text-sm inline-flex items-center justify-center gap-2"
              data-testid="mobile-nav-login"
            >
              <LogIn className="w-4 h-4" /> Login
            </Link>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function Header() {
  const { count, setCartOpen } = useCart();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-gray-100">
      <div className="max-w-7xl mx-auto h-16 md:h-20 px-4 md:px-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
          <img src={LOGO} alt="Mok.Jaa" className="h-9 w-9 rounded-full bg-black" />
          <div className="font-display font-black text-xl tracking-tight">Mok.Jaa</div>
        </Link>
        <nav className="hidden md:flex items-center gap-8 text-sm font-medium">
          <Link to="/menu" className="hover:text-black text-gray-600" data-testid="nav-menu">Menu</Link>
          <Link to="/account" className="hover:text-black text-gray-600" data-testid="nav-account">My Orders</Link>
          <a href="/admin/login" className="hover:text-black text-gray-500" data-testid="nav-admin">Admin</a>
        </nav>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCartOpen(true)}
            className="relative rounded-full p-2.5 hover:bg-gray-100 transition"
            data-testid="open-cart-btn"
            aria-label="Open cart"
          >
            <ShoppingBag className="w-5 h-5" />
            {count > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-black text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center" data-testid="cart-count">
                {count}
              </span>
            )}
          </button>
          {user ? (
            <div className="hidden md:flex items-center gap-2">
              <Link to="/account" className="rounded-full p-2.5 hover:bg-gray-100" data-testid="user-account-btn">
                <User2 className="w-5 h-5" />
              </Link>
              <button onClick={() => { logout(); navigate("/"); }} className="rounded-full p-2.5 hover:bg-gray-100" data-testid="logout-btn" aria-label="Logout">
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <Link
              to="/auth"
              className="hidden md:inline-flex bg-black text-white rounded-full px-5 py-2 text-sm font-bold hover:bg-gray-800 active:scale-[0.98] transition"
              data-testid="login-btn"
            >
              Login
            </Link>
          )}
          <button
            onClick={() => setMobileOpen(true)}
            className="md:hidden rounded-full p-2.5 hover:bg-gray-100"
            aria-label="Open menu"
            data-testid="mobile-menu-btn"
          >
            <MenuIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      <MobileDrawer open={mobileOpen} onClose={() => setMobileOpen(false)} user={user} logout={logout} />
    </header>
  );
}
